// "Merge into…" (kitchen flow, go-live; admin): fold one stock item into
// another. Pick the item to keep → the server works out the whole merge and
// rolls it back (preview) → Merge. When the units cannot be converted
// automatically, the admin says how many of the kept unit one merged unit is.
// Strings: lib/inventoryFlow/mergeStrings.ts.
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react'
import apiClient, { ApiClientError } from '@/api/client'
import type { StockItem } from '@/types'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useCurrency } from '@/contexts/CurrencyContext'
import { fmtQty, fmtQtyUnit } from '@/lib/quantity'
import { toastHelpers } from '@/lib/toast-helpers'
import { newIdempotencyKey } from '@/lib/inventoryFlow/offlineQueue'
import { MG } from '@/lib/inventoryFlow/mergeStrings'
import { describeMoved, mergeCandidates, unitsConvert } from '@/lib/inventoryFlow/mergeLogic'
import type { MergeResult } from '@/lib/inventoryFlow/mergeTypes'
import type { FlowItem } from '@/lib/inventoryFlow/types'

const STOCK_KEYS = ['stockItems', 'stockAlerts', 'stockSummary', 'advancedStockReport', 'admin-stock-item-options', 'inventory-flow']

export function MergeItemDialog({ item, onClose }: { item: StockItem | null; onClose: () => void }) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const [search, setSearch] = useState('')
  const [keep, setKeep] = useState<FlowItem | null>(null)
  const [factor, setFactor] = useState('')
  const [note, setNote] = useState('')
  const [preview, setPreview] = useState<MergeResult | null>(null)
  const [checking, setChecking] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [key, setKey] = useState(newIdempotencyKey)
  const { data } = useQuery({ queryKey: ['inventory-flow', 'items'], queryFn: () => apiClient.getFlowItems(), enabled: Boolean(item), staleTime: 60_000 })
  const matches = useMemo(
    () => (item ? mergeCandidates(data?.data ?? [], { id: item.id, is_returnable: item.is_returnable }, search) : []),
    [data, item, search],
  )
  const needsFactor = Boolean(item && keep && !unitsConvert(item.unit, keep.unit))
  const factorNum = Number(factor)
  const factorOk = !needsFactor || factorNum > 0

  const reset = () => {
    setSearch(''); setKeep(null); setFactor(''); setNote(''); setPreview(null); setError(null); setKey(newIdempotencyKey())
  }
  const close = () => { reset(); onClose() }

  // Preview whenever the pair (or the typed conversion) is ready.
  useEffect(() => {
    if (!item || !keep || !factorOk) {
      setPreview(null)
      return
    }
    let live = true
    setChecking(true)
    setError(null)
    const t = setTimeout(() => {
      apiClient.previewItemMerge({ survivor_id: keep.id, merged_id: item.id, units_per_merged: needsFactor ? factorNum : undefined })
        .then((r) => { if (live) setPreview(r.data ?? null) })
        .catch((e: Error) => { if (live) { setPreview(null); setError(e instanceof ApiClientError ? e.message.split(' — ')[0] : e.message) } })
        .finally(() => { if (live) setChecking(false) })
    }, needsFactor ? 400 : 0)
    return () => { live = false; clearTimeout(t) }
  }, [item, keep, needsFactor, factorNum, factorOk])

  const apply = async () => {
    if (!item || !keep || !preview) return
    setBusy(true)
    setError(null)
    try {
      const r = await apiClient.mergeItems(
        { survivor_id: keep.id, merged_id: item.id, units_per_merged: needsFactor ? factorNum : undefined, note: note.trim() || undefined }, key)
      for (const k of STOCK_KEYS) qc.invalidateQueries({ queryKey: [k] })
      toastHelpers.success('Inventory', MG.done(r.data?.merged_name ?? item.name, r.data?.survivor_name ?? keep.name))
      close()
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message.split(' — ')[0] : (e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => { if (!o) close() }}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{MG.title(item?.name ?? '')}</DialogTitle>
          <DialogDescription>{MG.help}</DialogDescription>
        </DialogHeader>

        {keep ? (
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-lg border p-3">
              <div>
                <p className="text-xs uppercase text-muted-foreground">{MG.mergeLabel}</p>
                <p className="font-semibold">{item?.name}</p>
                <p className="text-sm text-muted-foreground">{item && fmtQtyUnit(item.quantity_on_hand, item.unit)}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden />
              <div>
                <p className="text-xs uppercase text-muted-foreground">{MG.keepLabel}</p>
                <p className="font-semibold">{keep.name}</p>
                <Button variant="link" className="h-auto p-0 text-sm" onClick={() => { setKeep(null); setPreview(null); setFactor('') }}>{MG.change}</Button>
              </div>
            </div>
            {needsFactor && item && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="merge-factor">{MG.factorQuestion(item.unit, keep.unit)}</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm">1 {item.unit} =</span>
                  <Input id="merge-factor" type="number" min={0} step="any" inputMode="decimal" className="w-32" value={factor} onChange={(e) => setFactor(e.target.value)} />
                  <span className="text-sm">{keep.unit}</span>
                </div>
                <p className="text-xs text-muted-foreground">{MG.factorHelp}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <Input autoFocus placeholder={MG.search} value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="flex max-h-60 flex-wrap gap-2 overflow-y-auto">
              {search.trim() && matches.length === 0 && <p className="text-sm text-muted-foreground">{MG.noMatch}</p>}
              {matches.map((i) => (
                <button key={i.id} type="button" className="min-h-10 rounded-full border px-3 text-sm hover:bg-muted" onClick={() => setKeep(i)}>
                  {i.name} <span className="text-muted-foreground">({i.unit})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {checking && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {MG.checking}</p>}

        {preview && !checking && (
          <div className="space-y-2 rounded-lg bg-muted/40 p-3 text-sm">
            <p className="font-semibold">{MG.previewTitle}</p>
            {preview.factor !== 1 && <p>{MG.conversion(preview.merged_unit, preview.survivor_unit, fmtQty(preview.factor))}</p>}
            {preview.locations.length === 0 && <p>{MG.nothingInStock}</p>}
            {preview.locations.map((l) => (
              <p key={l.location_id ?? 'all'} className="tabular-nums">
                {MG.stockLine(fmtQtyUnit(l.qty_from, preview.merged_unit), fmtQtyUnit(l.qty_to, preview.survivor_unit), l.location_name)}
                <span className="text-muted-foreground"> · {formatCurrency(l.value)}</span>
              </p>
            ))}
            <p>{MG.onHandAfter(fmtQtyUnit(preview.survivor_on_hand_after, preview.survivor_unit))}</p>
            {describeMoved(preview.moved) && <p className="text-muted-foreground">{describeMoved(preview.moved)}</p>}
            {preview.warnings.map((w) => <p key={w} className="text-warning-fg">{w}</p>)}
          </div>
        )}

        {preview && (
          <Input placeholder={MG.note} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        )}

        {error && (
          <p className="flex items-start gap-2 rounded-md bg-danger-surface/50 px-3 py-2 text-sm text-danger-fg">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>{MG.cancel}</Button>
          <Button disabled={!preview || checking || busy} onClick={() => void apply()}>{busy ? MG.merging : MG.confirm}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
